import Long from 'long';
import * as THREE from 'three';
import { PointCloudTreeNode } from '../../PointCloudTree';
import { type ChunkData, GetChildId, getOctantsArray, GetRangeOfSubtree } from '../../ppcreader/index';
import { Utils } from '../../utils';
import { numNodesLoading, maxNodesLoading, incrementNumNodesLoading, decrementNumNodesLoading } from '../../potreeState';
import { DATA_DEPTH } from './constants';
import { PPCPointCloudGeometry } from './index';
import { PPCDataTile } from './PPCDataTile';

/** Node loading state returned by getLoadingState() */
interface NodeLoadingState {
  name: string;
  hierarchyChunkLoaded: boolean;
  hierarchyChunkFailed: boolean;
  hierarchyChunkRetryCount: number;
  hierarchyLoaded: boolean;
  pointsLoaded: boolean;
  loaded: boolean;
  loadFailed: boolean;
  loadRetryCount: number;
}

/** Point cloud geometry node for debug inspection */
interface DebugGeometryNode {
  getLoadingState?(): NodeLoadingState;
  children?: Record<string, DebugGeometryNode>;
}

/** Point cloud object for debug inspection */
interface DebugPointCloud {
  pcoGeometry: {
    root: DebugGeometryNode;
  };
}

declare global {
  interface Window {
    hierarchyLoadQueue?: HierarchyLoadQueue;
    findStuckNodes?: (pointcloud: DebugPointCloud) => {
      stuckNodes: NodeLoadingState[];
      pendingNodes: NodeLoadingState[];
      allNodes: NodeLoadingState[];
    };
  }
}

/**
 * Time-budgeted queue for hierarchy loading.
 * Processes hierarchy loads during the render loop with a time budget to prevent frame spikes.
 */
class HierarchyLoadQueue {
  private queue: PPCPointCloudGeometryNode[] = [];
  private processing = false;
  private timeBudgetMs = 4; // 4ms budget per frame (quarter of 16ms frame)

  enqueue(node: PPCPointCloudGeometryNode): void {
    // Don't add disposed nodes or duplicates
    if (node.isDisposed() || this.queue.includes(node)) {
      return;
    }
    this.queue.push(node);
    this.scheduleProcessing();
  }

  private scheduleProcessing(): void {
    if (this.processing || this.queue.length === 0) return;
    this.processing = true;
    requestAnimationFrame(() => this.processWithBudget());
  }

  private processWithBudget(): void {
    const startTime = performance.now();
    let processedCount = 0;

    while (this.queue.length > 0) {
      const elapsed = performance.now() - startTime;
      if (elapsed >= this.timeBudgetMs) {
        // Time budget exceeded, continue next frame
        break;
      }

      const node = this.queue.shift();
      if (!node) continue;
      // Skip disposed nodes to prevent processing stale references
      if (node.isDisposed()) {
        continue;
      }
      // Only process if hierarchy chunk is ready and hierarchy not yet loaded
      // Note: nodes should only be enqueued when hierarchyChunkLoaded is true,
      // so isHierarchyChunkReady() should always be true here
      if (!node.isHierarchyLoaded() && node.isHierarchyChunkReady()) {
        node.executeLoadHierarchy();
        processedCount++;
      }
    }

    // Track for debug widget
    if (processedCount > 0 && window.PPCLoaderDebugWidget !== undefined) {
      window.PPCLoaderDebugWidget.trackHierarchyLoad(performance.now() - startTime);
    }

    this.processing = false;

    // Continue processing if there's more in the queue
    if (this.queue.length > 0) {
      this.scheduleProcessing();
    }
  }

  getQueueSize(): number {
    return this.queue.length;
  }

  clear(): void {
    this.queue.length = 0;
    // Note: Don't reset processing flag here - let the current RAF callback finish naturally.
    // The disposed check in processWithBudget will skip any stale nodes.
  }
}

// Global hierarchy load queue
const hierarchyLoadQueue = new HierarchyLoadQueue();

// Expose for debug widget
window.hierarchyLoadQueue = hierarchyLoadQueue;

/**
 * Clear the hierarchy load queue. Call this when removing point clouds
 * to prevent stale nodes from being processed.
 */
export function clearHierarchyLoadQueue(): void {
  hierarchyLoadQueue.clear();
}

/**
 * Debug function to find stuck nodes in the tree.
 * Call from console: window.findStuckNodes(viewer.scene.pointclouds[0])
 */
window.findStuckNodes = function (pointcloud: DebugPointCloud) {
  const stuckNodes: NodeLoadingState[] = [];
  const pendingNodes: NodeLoadingState[] = [];
  const allNodes: NodeLoadingState[] = [];
  const queue: DebugGeometryNode[] = [pointcloud.pcoGeometry.root];

  while (queue.length > 0) {
    const node = queue.shift();
    if (!node) continue;

    const state = node.getLoadingState?.();
    if (state) {
      allNodes.push(state);

      // Node is permanently stuck if hierarchy chunk failed and exceeded retries
      const isPermanentlyStuck = (state.hierarchyChunkFailed && state.hierarchyChunkRetryCount >= 3) || (state.loadFailed && state.loadRetryCount >= 3);

      // Node is pending if hierarchy chunk not yet loaded and not failed
      const isPending = !state.hierarchyChunkLoaded && !state.hierarchyChunkFailed;

      if (isPermanentlyStuck) {
        stuckNodes.push(state);
      } else if (isPending) {
        pendingNodes.push(state);
      }
    }

    // Traverse children
    const children = node.children || {};
    for (const key in children) {
      if (children[key]) {
        queue.push(children[key]);
      }
    }
  }

  console.log('=== Node Status Summary ===');
  console.log('Total nodes:', allNodes.length);
  console.log('Permanently stuck (failed after retries):', stuckNodes.length, stuckNodes);
  console.log('Pending (waiting for hierarchy chunk):', pendingNodes.length, pendingNodes.slice(0, 10));
  console.log('To inspect a pending node, run: viewer.scene.pointclouds[0].pcoGeometry.root.children[X].getLoadingState()');

  return { stuckNodes, pendingNodes, allNodes };
};

const getOctantIndex = (octant: number) => {
  const octants = getOctantsArray();
  if (octant === octants[0]) return 0;
  else if (octant === octants[1]) return 4;
  else if (octant === octants[2]) return 2;
  else if (octant === octants[3]) return 6;
  else if (octant === octants[4]) return 1;
  else if (octant === octants[5]) return 5;
  else if (octant === octants[6]) return 3;
  else if (octant === octants[7]) return 7;
};

export class PPCPointCloudGeometryNode extends PointCloudTreeNode {
  static IDCount = 0;
  id: number;
  name: string;
  index: number;
  ppcGeometry: PPCPointCloudGeometry;
  geometry: THREE.BufferGeometry | null;
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  children: Record<number, PPCPointCloudGeometryNode>;
  numPoints: number;
  level: number;
  loaded: boolean;
  oneTimeDisposeHandlers: (() => void)[];
  spacing: number;
  tightBoundingBox?: THREE.Box3;
  tightBoundingSphere?: THREE.Sphere;
  parent?: PPCPointCloudGeometryNode;
  /**
   * Variables above this are used by Potree
   */
  private voxelId: Long;
  private chunkIndex: number;
  private firstChildChunkIndex: number;
  private childMask: number;
  private dataLevel: number;
  private childInfo: number[] | undefined;
  private tile3DOffsets: number[] | undefined;
  private hierarchyChunkLoaded = false;
  private hierarchyChunkFailed = false;
  private hierarchyChunkRetryCount = 0;
  private hierarchyLoaded = false;
  private pointsLoaded = false;
  private loadFailed = false;
  private loadRetryCount = 0;
  private _isDisposed = false;
  private _lastHierarchyRetryTime = 0;
  private _lastLoadRetryTime = 0;

  // Cancellation token for in-flight async operations
  private _loadGeneration = 0;

  /**
   * A data-tile load for this node is in flight. The visibility pass calls
   * load() on the top unloaded nodes EVERY frame until they are uploaded;
   * without this guard each freed loader slot was re-taken by a duplicate
   * fetch of a node already loading, starving every other node.
   */
  private _loadInFlight = false;

  // Cache mapping segment IDs to point indices for fast lookups
  private segmentToPointsCache: Map<number, number[]> | null = null;

  constructor(args: { voxelId: Long; chunkIndex: number; level: number; ppcGeometry: PPCPointCloudGeometry; boundingBox: THREE.Box3; name: string; index: number }) {
    super();
    const { voxelId, chunkIndex, level, ppcGeometry, name, boundingBox, index } = args;
    this.voxelId = voxelId;
    this.level = level;
    this.ppcGeometry = ppcGeometry;
    this.chunkIndex = chunkIndex;
    this.boundingBox = boundingBox;
    const chunkInfo = this.ppcGeometry.ppchReader.getNextChunkInfo(this.voxelId);
    this.loadHierarchyChunk(chunkInfo);
    this.children = {};
    this.firstChildChunkIndex = -1;
    this.childMask = -1;
    this.name = name;
    this.id = PPCPointCloudGeometryNode.IDCount++;
    this.index = index;
    this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());
    this.spacing = this.ppcGeometry.spacing / Math.pow(2, this.level);
    this.geometry = null;
    this.numPoints = -1;
    this.loaded = false;
    this.oneTimeDisposeHandlers = [];
    this.dataLevel = this.level + DATA_DEPTH;
  }

  isGeometryNode() {
    return true;
  }

  getLevel() {
    return this.level;
  }

  isTreeNode() {
    return false;
  }

  isLoaded() {
    return this.loaded;
  }

  getBoundingSphere() {
    return this.boundingSphere;
  }

  getBoundingBox() {
    return this.boundingBox;
  }

  getNumPoints() {
    if (!this.hierarchyChunkLoaded) return this.numPoints;
    if (!this.pointsLoaded) {
      this.loadNumPoints();
      this.pointsLoaded = true;
    }
    return this.numPoints;
  }

  getChildren() {
    // Lazy hierarchy loading: only load when visibility system requests children
    // AND the hierarchy chunk data is available
    if (!this.hierarchyLoaded && this.hierarchyChunkLoaded) {
      // Queue hierarchy loading to be processed with time budget
      hierarchyLoadQueue.enqueue(this);
    }

    // If hierarchy chunk failed, attempt retry on next visibility update
    if (this.hierarchyChunkFailed && !this.hierarchyChunkLoaded) {
      this.attemptHierarchyChunkRetry();
    }

    if (!this.hierarchyLoaded) return [];

    const children = [];
    for (let i = 0; i < 8; i++) {
      const child = this.children[i];
      // Include all children - LRU uses unloadForLRU() which keeps nodes in the tree
      // but with loaded=false, allowing them to be reloaded when visible again.
      // Only skip truly disposed nodes (from full cleanup, not LRU eviction).
      if (child && !child.isDisposed()) {
        children.push(child);
      }
    }

    return children;
  }

  /**
   * Attempt to retry hierarchy chunk loading if it failed.
   * Called automatically by getChildren() when in failed state.
   */
  private attemptHierarchyChunkRetry(): void {
    const MAX_RETRIES = 3;
    // Only retry if we haven't exceeded retry limit and not already retrying
    if (this.hierarchyChunkRetryCount >= MAX_RETRIES) {
      return; // Permanently failed
    }

    // Check if enough time has passed since last attempt (exponential backoff)
    const now = Date.now();
    const lastAttemptTime = this._lastHierarchyRetryTime || 0;
    const delay = Math.pow(2, this.hierarchyChunkRetryCount) * 1000;

    if (now - lastAttemptTime < delay) {
      return; // Not enough time passed
    }

    // Mark retry attempt
    this._lastHierarchyRetryTime = now;
    this.hierarchyChunkRetryCount++;
    this.hierarchyChunkFailed = false;

    // Retry loading
    const chunkInfo = this.ppcGeometry.ppchReader.getNextChunkInfo(this.voxelId);
    this.loadHierarchyChunk(chunkInfo);
  }

  /**
   * Check if hierarchy has been loaded for this node.
   * Used by HierarchyLoadQueue to avoid duplicate processing.
   */
  isHierarchyLoaded(): boolean {
    return this.hierarchyLoaded;
  }

  /**
   * Check if hierarchy chunk data is ready to be processed.
   * Used by HierarchyLoadQueue to skip nodes that aren't ready.
   */
  isHierarchyChunkReady(): boolean {
    return this.hierarchyChunkLoaded;
  }

  /**
   * Check if this node has been disposed.
   * Used by HierarchyLoadQueue to skip disposed nodes.
   */
  isDisposed(): boolean {
    return this._isDisposed;
  }

  /**
   * Execute the actual hierarchy loading.
   * Called by HierarchyLoadQueue when processing with time budget.
   */
  executeLoadHierarchy(): void {
    if (this.hierarchyLoaded || !this.hierarchyChunkLoaded) return;
    this.loadHierarchy();
    this.hierarchyLoaded = true;
  }

  private getDataTile() {
    const tileValues = this.tile3DOffsets;
    if (this.dataLevel > this.ppcGeometry.depth) return;
    if (!tileValues?.length) return;
    const [fromVoxelId] = GetRangeOfSubtree(this.voxelId, this.dataLevel);
    const chunkInfo = this.ppcGeometry.ppchReader.getNextChunkInfo(fromVoxelId);
    if (!chunkInfo) return;
    const fromIndex = tileValues[this.chunkIndex - 1];
    let nextIndex = tileValues[this.chunkIndex];
    if (nextIndex === 1) nextIndex = chunkInfo.mNrVoxels;
    const dataChunk = this.ppcGeometry.getDataChunk(chunkInfo);
    const tile = new PPCDataTile({ dataChunk, fromIndex, toIndex: nextIndex - 1, node: this });
    this.numPoints = tile.numPoints;
    return tile;
  }

  private loadNumPoints() {
    const tile = this.getDataTile();
    if (!tile) return;
    this.numPoints = tile.numPoints;
  }

  loadHierarchy() {
    const childValues = this.childInfo;
    if (this.dataLevel >= this.ppcGeometry.depth) return;
    if (!childValues?.length) return;
    this.childMask = childValues[2 * this.chunkIndex - 2];
    this.firstChildChunkIndex = childValues[2 * this.chunkIndex - 1];
    if (this.childMask <= 0 || this.firstChildChunkIndex === -1) return;

    const octants = getOctantsArray();
    let count = 0;
    for (let octant = 0; octant < octants.length; octant++) {
      const octantSet = (1 << octant) & this.childMask;
      if (octantSet) {
        const index = getOctantIndex(octant) || 0;
        const node = new PPCPointCloudGeometryNode({
          index,
          boundingBox: Utils.createChildAABB(this.boundingBox, index),
          chunkIndex: this.firstChildChunkIndex + count,
          level: this.level + 1,
          name: this.name + index,
          ppcGeometry: this.ppcGeometry,
          voxelId: GetChildId(this.voxelId, octant),
        });
        this.children[node.index] = node;
        node.parent = this;
        count++;
      }
    }
  }

  /**
   * Load the hierarchy chunk with error handling and retry logic.
   * This method is called from the constructor and handles failures gracefully.
   * Retries are handled by attemptHierarchyChunkRetry() called from getChildren().
   */
  private loadHierarchyChunk(chunkInfo: ChunkData | undefined): void {
    if (!chunkInfo) return;
    // Increment generation to invalidate any in-flight loads
    const currentGeneration = ++this._loadGeneration;

    const hierarchyChunk = this.ppcGeometry.getHierarchyChunk(chunkInfo);

    hierarchyChunk
      .load()
      .then((args) => {
        // Check if this load is still valid (node not disposed, generation matches)
        if (this._isDisposed || currentGeneration !== this._loadGeneration) {
          return; // Stale load, ignore results
        }
        this.childInfo = args.childInfo;
        this.tile3DOffsets = args.tile3DOffsets;
        this.hierarchyChunkLoaded = true;
        this.hierarchyChunkFailed = false;
        this.hierarchyChunkRetryCount = 0;
      })
      .catch((error) => {
        // Check if this load is still valid
        if (this._isDisposed || currentGeneration !== this._loadGeneration) {
          return; // Stale load, ignore error
        }
        console.error(`Hierarchy chunk load failed for node ${this.name}:`, error);
        this.hierarchyChunkFailed = true;
        this._lastHierarchyRetryTime = Date.now();
        // Retry will be triggered by next visibility update call to getChildren()
      });
  }

  /**
   * Check if hierarchy chunk loading has permanently failed (after all retries).
   */
  isHierarchyChunkFailed(): boolean {
    return this.hierarchyChunkFailed && this.hierarchyChunkRetryCount >= 3;
  }

  /**
   * Manually retry loading the hierarchy chunk if it failed.
   * Returns true if retry was initiated, false if not applicable.
   */
  retryHierarchyChunk(): boolean {
    if (!this.hierarchyChunkFailed || this.hierarchyChunkLoaded) {
      return false;
    }

    this.hierarchyChunkRetryCount = 0;
    this.hierarchyChunkFailed = false;
    const chunkInfo = this.ppcGeometry.ppchReader.getNextChunkInfo(this.voxelId);
    this.loadHierarchyChunk(chunkInfo);
    return true;
  }

  async load() {
    // Don't load if disposed
    if (this._isDisposed) return;

    if (!this.hierarchyChunkLoaded || !this.hierarchyLoaded || !this.pointsLoaded) return;

    // Don't retry if we've failed too many times
    const MAX_LOAD_RETRIES = 3;
    if (this.loadFailed && this.loadRetryCount >= MAX_LOAD_RETRIES) return;

    // If failed but retry not yet due, check if enough time has passed
    if (this.loadFailed) {
      const now = Date.now();
      const lastAttemptTime = this._lastLoadRetryTime || 0;
      const delay = Math.pow(2, this.loadRetryCount) * 1000;
      if (now - lastAttemptTime < delay) {
        return; // Not enough time passed for retry
      }
      // Time has passed, allow retry
      this._lastLoadRetryTime = now;
      this.loadFailed = false;
    }

    const dataTile = this.getDataTile();
    if (this._loadInFlight || numNodesLoading >= maxNodesLoading || !dataTile || this.loaded) return;

    incrementNumNodesLoading();
    this._loadInFlight = true;
    this.geometry = new THREE.BufferGeometry();

    try {
      // Load the tile data - geometry processing is queued and will complete
      // later. The callback fires exactly once (see PPCDataTile.load): it is
      // the single place the in-flight slot is released on the non-throwing
      // path, whether the tile was processed or abandoned.
      await dataTile.load((loaded) => {
        this._loadInFlight = false;
        decrementNumNodesLoading();
        if (!loaded || this._isDisposed) return;
        // NOTE: Do NOT clear childInfo and tile3DOffsets here!
        // They are needed if LRU unloads this node and it needs to reload later.
        // They will be cleared only on full dispose().
        this.loaded = true;
        this.loadFailed = false;
        this.loadRetryCount = 0;
      });
    } catch (error) {
      // Don't log error if disposed (expected during cleanup)
      if (!this._isDisposed) {
        console.error(`Node load failed for ${this.name}:`, error);
      }
      this._loadInFlight = false;
      decrementNumNodesLoading();
      this.loadFailed = true;
      this.loadRetryCount++;
      this._lastLoadRetryTime = Date.now();
      // Retry will be triggered by next visibility update call to load()
    }
  }

  /**
   * Check if this node's data loading has permanently failed.
   */
  isLoadFailed(): boolean {
    return this.loadFailed && this.loadRetryCount >= 3;
  }

  /**
   * Get detailed loading state for debugging.
   * Useful for diagnosing stuck nodes.
   */
  getLoadingState(): {
    name: string;
    hierarchyChunkLoaded: boolean;
    hierarchyChunkFailed: boolean;
    hierarchyChunkRetryCount: number;
    hierarchyLoaded: boolean;
    pointsLoaded: boolean;
    loaded: boolean;
    loadFailed: boolean;
    loadRetryCount: number;
  } {
    return {
      name: this.name,
      hierarchyChunkLoaded: this.hierarchyChunkLoaded,
      hierarchyChunkFailed: this.hierarchyChunkFailed,
      hierarchyChunkRetryCount: this.hierarchyChunkRetryCount,
      hierarchyLoaded: this.hierarchyLoaded,
      pointsLoaded: this.pointsLoaded,
      loaded: this.loaded,
      loadFailed: this.loadFailed,
      loadRetryCount: this.loadRetryCount,
    };
  }

  /**
   * Manually retry loading this node's data.
   * Returns true if retry was initiated, false if not applicable.
   */
  retryLoad(): boolean {
    if (!this.loadFailed || this.loaded) {
      return false;
    }

    this.loadRetryCount = 0;
    this.loadFailed = false;
    this.load();
    return true;
  }

  /**
   * Sets the segment cache for this node. Called during data loading.
   * @param cache Map of segment IDs to point indices
   */
  setSegmentCache(cache: Map<number, number[]>): void {
    this.segmentToPointsCache = cache;
  }

  /**
   * Gets all point indices for a given segment ID.
   * Returns an empty array if the segment is not found or cache not built.
   * @param segmentId The segment ID to look up
   * @returns Array of point indices belonging to this segment
   */
  getPointIndicesForSegment(segmentId: number): number[] {
    if (!this.segmentToPointsCache) {
      return [];
    }
    return this.segmentToPointsCache.get(segmentId) || [];
  }

  /**
   * Gets all points for a given segment ID in world space.
   * @param segmentId The segment ID to look up
   * @param matrixWorld Optional world matrix to transform points (from sceneNode)
   * @returns Array of points in world space
   */
  getPointsForSegment(segmentId: number, matrixWorld?: THREE.Matrix4): THREE.Vector3[] {
    const indices = this.getPointIndicesForSegment(segmentId);
    if (indices.length === 0 || !this.geometry?.attributes?.position) {
      return [];
    }

    const positionAttribute = this.geometry.attributes.position;
    const positionArray = positionAttribute.array;
    const points: THREE.Vector3[] = [];

    for (const index of indices) {
      const point = new THREE.Vector3(positionArray[3 * index + 0], positionArray[3 * index + 1], positionArray[3 * index + 2]);

      // Transform to world space if matrix provided
      if (matrixWorld) {
        point.applyMatrix4(matrixWorld);
      }

      points.push(point);
    }

    return points;
  }

  /**
   * Unload geometry for LRU memory management.
   * This frees GPU memory but keeps the node in a state where it can be reloaded
   * when the camera moves back to this area. Used during 3D navigation.
   */
  unloadForLRU() {
    // Increment generation to cancel any in-flight async operations
    this._loadGeneration++;

    if (this.geometry) {
      this.unload();

      // Explicitly dispose all buffer attributes to free GPU memory
      const attributes = this.geometry.attributes;
      for (const name in attributes) {
        const attribute = attributes[name];
        if (attribute && attribute.array) {
          // Nullify the typed array reference to help GC
          (attribute as unknown as { array: null }).array = null;
        }
      }

      // Dispose index buffer if present
      if (this.geometry.index) {
        (this.geometry.index as unknown as { array: null }).array = null;
      }

      this.geometry.dispose();
      this.geometry = null;
      this.loaded = false;
    }

    // Clear segment cache to free memory
    this.segmentToPointsCache = null;

    // NOTE: Keep hierarchyChunkLoaded, hierarchyLoaded, childInfo, tile3DOffsets
    // so the node can reload its data tile when needed again.
    // Also keep pointsLoaded (numPoints was already calculated).
  }

  /**
   * Permanently dispose this node. Used when removing a point cloud entirely.
   * After calling this, the node cannot be reloaded and should be garbage collected.
   */
  dispose() {
    this._isDisposed = true;

    // Increment generation to cancel any in-flight async operations
    this._loadGeneration++;

    if (this.geometry) {
      this.unload();

      // Explicitly dispose all buffer attributes to free GPU memory
      const attributes = this.geometry.attributes;
      for (const name in attributes) {
        const attribute = attributes[name];
        if (attribute && attribute.array) {
          // Nullify the typed array reference to help GC
          (attribute as unknown as { array: null }).array = null;
        }
      }

      // Dispose index buffer if present
      if (this.geometry.index) {
        (this.geometry.index as unknown as { array: null }).array = null;
      }

      this.geometry.dispose();
      this.geometry = null;
      this.loaded = false;
    }

    // Clear hierarchy data to free memory
    this.childInfo = undefined;
    this.tile3DOffsets = undefined;

    // Clear segment cache on disposal
    this.segmentToPointsCache = null;

    // NOTE: Do NOT clear this.children = {} here!
    // LRU.disposeDescendants() needs to iterate children after dispose() is called.
    // Children will be garbage collected when the entire tree is unreachable.
  }

  unload() {
    this.loaded = false;
    this.dispatchEvent({ type: 'dispose' });
    for (let i = 0; i < this.oneTimeDisposeHandlers.length; i++) {
      const handler = this.oneTimeDisposeHandlers[i];
      handler();
    }
    this.oneTimeDisposeHandlers = [];
    // Clear segment cache on unload
    this.segmentToPointsCache = null;
  }
}
